import { useCallback, useRef, useState } from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';

import { setSessionCompany } from '../../../../actions/session';
import {
  Company,
  type CompanyVerificationInfo,
} from '../../../../common/interfaces/entities';
import { getSessionCompany } from '../../../../selectors/session';
import {
  CompanyVerificationPollingMode,
  CompanyVerificationPollingResult,
  shouldResolveCompanyVerificationPolling,
} from '../companyVerificationPolling';

export {
  COMPANY_VERIFICATION_POLLING_TIMEOUT_IN_MS,
  CompanyVerificationPollingMode,
  shouldResolveCompanyVerificationPolling,
} from '../companyVerificationPolling';

const query = gql`
  query getCompanyById($id: String!) {
    getCompanyById(id: $id) {
      name
      status
      verificationInfo {
        ...companyVerificationInfoFragment
      }
    }
  }
`;

type QueryOutput = {
  getCompanyById: Pick<Company, 'name' | 'status'> & {
    verificationInfo: CompanyVerificationInfo;
  };
};

type QueryInput = {
  id: string;
};

const POLLING_INTERVAL_IN_MS = 1000 * 2;

export const useGetCompanyVerificationInfoWithPolling = () => {
  const sessionCompany = useSelector(getSessionCompany);
  const client = useApolloClient();
  const dispatch = useDispatch();
  const [isPollingRunning, setIsPollingRunning] = useState(false);
  const resolvePollingPromiseRef = useRef<
    ((value: CompanyVerificationPollingResult) => void) | null
  >(null);
  const pollingModeRef = useRef<CompanyVerificationPollingMode | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestPollingResultRef =
    useRef<CompanyVerificationPollingResult | null>(null);
  const stopPollingRef = useRef<(() => void) | null>(null);

  const completePolling = useCallback(
    (value: CompanyVerificationPollingResult) => {
      stopPollingRef.current?.();
      setIsPollingRunning(false);
      pollingModeRef.current = null;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (resolvePollingPromiseRef.current) {
        resolvePollingPromiseRef.current(value);
        resolvePollingPromiseRef.current = null;
      }
    },
    []
  );

  const { data, startPolling, stopPolling } = useQuery<QueryOutput, QueryInput>(
    query,
    {
      variables: {
        id: sessionCompany?.id,
      },
      skip: !sessionCompany?.id,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      onCompleted: data => {
        const { status, verificationInfo, name } = data.getCompanyById;
        const latestPollingResult = { status, verificationInfo };
        latestPollingResultRef.current = latestPollingResult;

        if (
          pollingModeRef.current &&
          shouldResolveCompanyVerificationPolling({
            ...latestPollingResult,
            mode: pollingModeRef.current,
          })
        ) {
          completePolling(latestPollingResult);
        }

        // Update company name if it has been changed
        dispatch(setSessionCompany({ ...sessionCompany, name }));

        // This prevents the polling query from creating a separate Query-level cache entry
        // that would trigger refetches in useGetCompanyById
        client.cache.modify({
          id: client.cache.identify({
            __typename: 'Company',
            id: sessionCompany?.id,
          }),
          fields: {
            status() {
              return status;
            },
            verificationInfo(existing: unknown) {
              return verificationInfo ?? existing;
            },
          },
        });
      },
    }
  );
  stopPollingRef.current = stopPolling;

  const status = data?.getCompanyById?.status;
  const verificationInfo = data?.getCompanyById?.verificationInfo;

  const waitForVerificationStatus = useCallback(
    ({
      mode = CompanyVerificationPollingMode.ID_LEGAL_DOCUMENT,
      timeoutInMs,
      fallbackValue,
    }: {
      mode?: CompanyVerificationPollingMode;
      timeoutInMs?: number;
      fallbackValue?: CompanyVerificationPollingResult;
    } = {}) =>
      new Promise<CompanyVerificationPollingResult>(resolve => {
        pollingModeRef.current = mode;
        resolvePollingPromiseRef.current = resolve;
        latestPollingResultRef.current = null;
        startPolling(POLLING_INTERVAL_IN_MS);
        setIsPollingRunning(true);

        if (timeoutInMs) {
          timeoutRef.current = setTimeout(() => {
            const value = latestPollingResultRef.current ?? fallbackValue;

            if (value) {
              completePolling(value);
            }
          }, timeoutInMs);
        }

        return { status, verificationInfo };
      }),
    [completePolling, startPolling, status, verificationInfo]
  );

  return {
    isPollingRunning,
    waitForVerificationStatus,
  };
};
